import { Capacitor } from '@capacitor/core';
import { Purchases } from '@revenuecat/purchases-capacitor';
import { safeStorage } from 'common/utils/safeStorage';
import { Mixpanel } from 'common/utils/mixpanel';

/*
 * Partner and campaign tagging.
 *
 * A person who arrives from a partner (Name Switch) or an event QR code carries a
 * campaign tag. The paywall hands it to RevenueCat as the `campaign` customer
 * attribute, and a RevenueCat targeting rule on that attribute shows the partner's
 * own offering and paywall. No paywall choice is made in app code, so a new
 * partner is a dashboard change, not a release.
 *
 * Ways in:
 *   https://app.doqit.io/partner/<slug>   (universal link, and the web app route)
 *   doqit://partner/<slug>, doqit-app://partner/<slug>
 *   any app link with ?campaign=<slug>    (the original event QR codes)
 *   an Adjust link with that deep link, which survives a fresh App Store install
 *     (deferred deep link), or an Adjust tracker whose network is a partner slug
 *
 * The tag is kept on the device for 60 days: sessionStorage alone (the old
 * behaviour) was lost as soon as the app was closed, so someone who tapped the
 * partner link today and subscribed tomorrow got the normal paywall.
 */

const STORAGE_KEY = 'doqit_campaign';
// Read by older code paths; still written so they keep working.
export const CAMPAIGN_SESSION_KEY = '_campaign_token';
const KEEP_MS = 60 * 24 * 60 * 60 * 1000;
const VALID = /^[a-z0-9][a-z0-9_-]{1,39}$/;

/** Partners with their own paywall. Adjust trackers named after one also tag the person. */
export const PARTNER_SLUGS = ['nameswitch'];

const clean = (value: unknown): string | null => {
  const v = String(value ?? '').trim().toLowerCase();
  return VALID.test(v) ? v : null;
};

export const getCampaign = (): string | null => {
  try {
    const session = clean(sessionStorage.getItem(CAMPAIGN_SESSION_KEY));
    if (session) return session;
  } catch {
    // sessionStorage unavailable: fall through
  }
  try {
    const raw = safeStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const { value, at } = JSON.parse(raw) as { value?: string; at?: number };
    if (!value || !at || Date.now() - at > KEEP_MS) return null;
    return clean(value);
  } catch {
    return null;
  }
};

/**
 * Remember a campaign tag and tell RevenueCat and Mixpanel straight away. Safe to
 * call repeatedly; never throws. Returns the stored value, or null if invalid.
 */
export const setCampaign = (value: unknown, source: string): string | null => {
  const campaign = clean(value);
  if (!campaign) return null;
  try { sessionStorage.setItem(CAMPAIGN_SESSION_KEY, campaign); } catch { /* best effort */ }
  try { safeStorage.setItem(STORAGE_KEY, JSON.stringify({ value: campaign, at: Date.now() })); } catch { /* best effort */ }
  try {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Mixpanel.register({ campaign, campaign_source: source });
  } catch { /* tracking never throws */ }
  if (Capacitor.isNativePlatform()) {
    // RevenueCat targeting can only read its reserved attributes, so the tag goes
    // on $campaign (what the targeting rule matches) as well as the custom one.
    Purchases.setAttributes({ campaign }).catch(() => undefined);
    Purchases.setCampaign({ campaign }).catch(() => undefined);
  }
  return campaign;
};

/** The stored campaign if it is a partner with its own paywall, else null. */
export const getPartner = (): string | null => {
  const campaign = getCampaign();
  return campaign && PARTNER_SLUGS.includes(campaign) ? campaign : null;
};

/**
 * The offering to show: the partner's own offering when this person carries a
 * partner tag and RevenueCat has an offering with that identifier, otherwise the
 * one RevenueCat chose (`current`). Picked in the app as well as by the targeting
 * rule because a RevenueCat experiment (the trial-length test) takes priority over
 * targeting, and would otherwise hide the partner offer from new people.
 */
/**
 * For a paywall that is already up and re-showing itself (the hard paywall loop):
 * if the person picked up a partner tag since it first opened (they left, tapped
 * the partner link, came back), the partner's offering to switch to; else null.
 * The open sheet cannot be closed from the app, so the switch happens on the
 * next re-show, right after they close it.
 */
export const partnerOfferingSince = async <T extends { identifier: string }>(
  current: T | null | undefined,
  load: () => Promise<{ all?: Record<string, T> } | null | undefined>,
): Promise<T | null> => {
  const partner = getPartner();
  if (!partner || current?.identifier === partner) return null;
  try {
    const offerings = await load();
    return offerings?.all?.[partner] ?? null;
  } catch {
    return null;
  }
};

export const pickOffering = <T extends { identifier: string }>(
  offerings: { current?: T | null; all?: Record<string, T> } | null | undefined,
): T | null | undefined => {
  const partner = getPartner();
  const own = partner ? offerings?.all?.[partner] : undefined;
  return own ?? offerings?.current;
};

/** The campaign a link carries: /partner/<slug> on any of our link forms, or ?campaign=. */
export const campaignFromUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const fromQuery = clean(parsed.searchParams.get('campaign'));
    if (fromQuery) return fromQuery;
    // https://app.doqit.io/partner/<slug> has the slug in the path; doqit://partner/<slug>
    // has "partner" as the host.
    const path = parsed.protocol === 'https:' || parsed.protocol === 'http:'
      ? parsed.pathname
      : `/${parsed.hostname}${parsed.pathname}`;
    const m = /^\/partner\/([a-z0-9_-]{2,40})\/?$/i.exec(path);
    return m ? clean(m[1]) : null;
  } catch {
    return null;
  }
};

/** An Adjust tracker or network named after a partner ("Name Switch", "nameswitch") tags them. */
export const campaignFromAttribution = (...names: Array<string | null | undefined>): string | null => {
  const squashed = names.map((name) => String(name ?? '').toLowerCase().replace(/[^a-z0-9]/g, ''));
  return PARTNER_SLUGS.find((slug) => squashed.some((n) => n === slug || n.startsWith(slug))) ?? null;
};
